<nav aria-label="breadcrumb">
  <ol class="pt-breadcrumb">
    <li class="breadcrumb-item">
      <a routerLink="/admin/overview/users" i18n>Users</a>
    </li>

    @if (isCreation()) {
      <li class="breadcrumb-item" i18n>Create</li>
    } @else {
      <li class="breadcrumb-item" i18n>Edit</li>

      <li class="breadcrumb-item" aria-current="page">
        @if (user) {
          <a [routerLink]="[ '/a', user?.username ]">{{ user?.username }}</a>
        }
      </li>
    }
  </ol>
</nav>

<ng-template #dashboard>
  @if (!isCreation() && user) {
    <div class="dashboard">
      <div>
        <a>
          <div class="dashboard-num">{{ user.videosCount }} ({{ user.videoQuotaUsed | bytes: 0 }})</div>
          <div class="dashboard-label" i18n>{user.videosCount, plural, =1 {Video} other {Videos}}</div>
        </a>
      </div>
      <div>
        <a>
          <div class="dashboard-num">{{ user.videoChannels.length || 0 }}</div>
          <div class="dashboard-label" i18n>{user.videoChannels.length, plural, =1 {Channel} other {Channels}}</div>
        </a>
      </div>
      <div>
        <a>
          <div class="dashboard-num">{{ subscribersCount }}</div>
          <div class="dashboard-label" i18n>{subscribersCount, plural, =1 {Subscriber} other {Subscribers}}</div>
        </a>
      </div>
      <div>
        <a [routerLink]="[ '/admin/moderation/abuses/list' ]" [queryParams]="{ 'search': 'reportee:&quot;' + user?.account.displayName + '&quot;' }">
          <div class="dashboard-num">{{ user.abusesCount }}</div>
          <div class="dashboard-label" i18n>Incriminated in reports</div>
        </a>
      </div>
      <div>
        <a [routerLink]="[ '/admin/moderation/abuses/list' ]" [queryParams]="{ 'search': 'reporter:&quot;' + user?.account.displayName + '&quot; state:accepted' }">
          <div class="dashboard-num">{{ user.abusesAcceptedCount }} / {{ user.abusesCreatedCount }}</div>
          <div class="dashboard-label" i18n>Authored reports accepted</div>
        </a>
      </div>
      <div>
        <a>
          <div class="dashboard-num">{{ user.videoCommentsCount }}</div>
          <div class="dashboard-label" i18n>{user.videoCommentsCount, plural, =1 {Comment} other {Comments}}</div>
        </a>
      </div>
    </div>
  }
</ng-template>

@if (error) {
  <my-alert type="danger">{{ error }}</my-alert>
}

<div class="pt-two-cols d-xxl-none"> <!-- hidden on large screens, as it is then displayed on the right side of the form -->
  <div class="col-0 col-xl-3"></div>

  <div class="col-12 col-xl-9">
    <ng-template *ngTemplateOutlet="dashboard"></ng-template>
  </div>
</div>

<div class="pt-two-cols mt-4"> <!-- user grid -->
  <div class="title-col">
    <div class="anchor" id="user"></div> <!-- user anchor -->

    @if (isCreation()) {
      <h2 i18n>NEW USER</h2>
    } @else if (user) {
      <h2 class="visually-hidden">EDIT USER {{ user.username }}</h2>

      <my-actor-avatar-edit
        actorType="account" [displayName]="user.account.displayName" [avatars]="user.account.avatars"
        editable="false" [username]="user.username" displayUsername="false"
      ></my-actor-avatar-edit>
    }
  </div>

  <div class="content-col">
    <div class="row">
      <form class="col" (ngSubmit)="formValidated()" [formGroup]="form">
        @if (isCreation()) {
          <div class="form-group">
            <label i18n for="username">Username</label>
            <input
              type="text" id="username" i18n-placeholder placeholder="john" class="form-control"
              formControlName="username" [ngClass]="{ 'input-error': formErrors['username'] }"
              >
            @if (formErrors.username) {
              <div class="form-error" role="alert">
                {{ formErrors.username }}
              </div>
            }
          </div>
        }

        @if (isCreation()) {
          <div class="form-group">
            <label i18n for="channelName">Channel name</label>
            <input
              type="text" id="channelName" i18n-placeholder placeholder="john_channel" class="form-control"
              formControlName="channelName" [ngClass]="{ 'input-error': formErrors['channelName'] }"
              >
            @if (formErrors.channelName) {
              <div class="form-error" role="alert">
                {{ formErrors.channelName }}
              </div>
            }
          </div>
        }

        <div class="form-group">
          <label i18n for="email">Email</label>
          <input
            type="text" id="email" i18n-placeholder placeholder="mail@example.com" class="form-control"
            formControlName="email" [ngClass]="{ 'input-error': formErrors['email'] }"
            autocomplete="off" [readonly]="user && user.pluginAuth !== null"
            >
          @if (formErrors.email) {
            <div class="form-error" role="alert">
              {{ formErrors.email }}
            </div>
          }
        </div>

        @if (isCreation()) {
          <div class="form-group">
            <label i18n for="password">Password</label>
            @if (isPasswordOptional()) {
              <div class="form-group-description" i18n>
              If you leave the password empty, an email will be sent to the user.
            </div>
            }
            <my-input-text formControlName="password" inputId="password" [formError]="formErrors['password']" autocomplete="new-password"></my-input-text>
          </div>
        }

        <div class="form-group">
          <label i18n for="role">Role</label>
          <div class="peertube-select-container">
            <select id="role" formControlName="role" class="form-control">
              @for (role of roles; track role) {
                <option [value]="role.value">
                  {{ role.label }}
                </option>
              }
            </select>
          </div>

          @if (formErrors.role) {
            <div class="form-error" role="alert">
              {{ formErrors.role }}
            </div>
          }
        </div>

        <div class="form-group">
          <label i18n id="videoQuotaLabel" for="videoQuota">Video quota</label>

          <my-select-custom-value
            labelId="videoQuotaLabel"
            inputId="videoQuota"
            [items]="videoQuotaOptions"
            formControlName="videoQuota"
            i18n-inputSuffix inputSuffix="bytes" inputType="number"
            [clearable]="false"
          ></my-select-custom-value>

          <my-user-real-quota-info [videoQuota]="getUserVideoQuota()"></my-user-real-quota-info>

          @if (formErrors.videoQuota) {
            <div class="form-error" role="alert">
              {{ formErrors.videoQuota }}
            </div>
          }
        </div>

        <div class="form-group">
          <label i18n id="videoQuotaDailyLabel" for="videoQuotaDaily">Daily video quota</label>

          <my-select-custom-value
            labelId="videoQuotaDailyLabel"
            inputId="videoQuotaDaily"
            [items]="videoQuotaDailyOptions"
            formControlName="videoQuotaDaily"
            i18n-inputSuffix inputSuffix="bytes" inputType="number"
            [clearable]="false"
          ></my-select-custom-value>

          @if (formErrors.videoQuotaDaily) {
            <div class="form-error" role="alert">
              {{ formErrors.videoQuotaDaily }}
            </div>
          }
        </div>

        @if (!isCreation() && getAuthPlugins().length !== 0) {
          <div class="form-group">
            <label i18n for="pluginAuth">Auth plugin</label>
            <div class="peertube-select-container">
              <select id="pluginAuth" formControlName="pluginAuth" class="form-control">
                <option [value]="null" i18n>None (local authentication)</option>
                @for (authPlugin of getAuthPlugins(); track authPlugin) {
                  <option [value]="authPlugin">{{ authPlugin }}</option>
                }
              </select>
            </div>
          </div>
        }

        <div class="form-group">
          <my-peertube-checkbox
            inputName="byPassAutoBlock" formControlName="byPassAutoBlock"
            i18n-labelText labelText="User bypasses video validation by moderators"
            >
            <ng-container ngProjectAs="description" i18n>
              Enable this checkbox if this user doesn't need to have its videos reviewed by moderators.
            </ng-container>
          </my-peertube-checkbox>
        </div>

        <input class="peertube-button primary-button" type="submit" value="{{ getFormButtonTitle() }}" [disabled]="!form.valid">
      </form>

      <div class="d-none d-xxl-block col-7">
        <ng-template *ngTemplateOutlet="dashboard"></ng-template>
      </div>
    </div>
  </div>
</div>

@if (displayTokenSessions()) {
  <div class="pt-two-cols mt-5">
    <div class="title-col">
      <div class="anchor" id="token-sessions"></div>
      <h2 i18n>TOKEN SESSIONS</h2>
    </div>

    <div class="content-col">
      <my-account-token-sessions [user]="user"></my-account-token-sessions>
    </div>
  </div>
}

@if (displayPasswordZone()) {
  <div class="pt-two-cols mt-5">
    <div class="title-col">
      <div class="anchor" id="danger"></div>
      <h2 i18n class="pt-title-danger">DANGER ZONE</h2>
    </div>

    <div class="content-col">
      <div class="form-group">
        <div class="mb-1 fw-bold" i18n>Send a link to reset the password by email to the user</div>
        <button class="peertube-button danger-button" (click)="resetPassword()" i18n>Ask for new password</button>
      </div>

      <div class="form-group">
        <div class="mb-1 fw-bold" i18n>Manually set the user password</div>
        <my-user-password [userId]="user.id" [username]="user.username"></my-user-password>
      </div>

      @if (user.twoFactorEnabled) {
        <div class="form-group">
          <div class="mb-1 fw-bold" i18n>This user has two factor authentication enabled</div>
          <button class="peertube-button danger-button" (click)="disableTwoFactorAuth()" i18n>Disable two factor authentication</button>
        </div>
      }
    </div>
  </div>
}
